import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend, Cell, PieChart, Pie } from 'recharts';
import { Megaphone, RefreshCw, Calendar, TrendingUp, DollarSign, MousePointer, Eye, Target, Loader, Search } from 'lucide-react';
import api from '../utils/api';
import { formatNumber, formatMoney } from '../utils/constants';
import toast from 'react-hot-toast';

const tooltipStyle = { background: '#1c2235', border: '1px solid #252d3d', borderRadius: 8, fontFamily: 'Raleway', color: '#e8ecf4' };
const tickStyle = { fill: '#c8d0e0', fontSize: 11 };
const campaignColors = ['#2E7DE2', '#22c55e', '#f97316', '#a855f7', '#eab308', '#ef4444', '#06b6d4', '#ec4899'];
const PRESETS = [
  { key: 'last_7d', label: '7 días' }, { key: 'last_30d', label: '30 días' },
  { key: 'last_90d', label: '90 días' }, { key: 'this_month', label: 'Este mes' },
  { key: 'last_month', label: 'Mes pasado' }, { key: 'custom', label: 'Personalizado' },
];

function getDateParams(preset, dateRange) {
  const today = new Date();
  if (preset === 'last_7d') { const d = new Date(today); d.setDate(d.getDate()-7); return { start_date: d.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0] }; }
  if (preset === 'last_30d') { const d = new Date(today); d.setDate(d.getDate()-30); return { start_date: d.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0] }; }
  if (preset === 'last_90d') { const d = new Date(today); d.setDate(d.getDate()-90); return { start_date: d.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0] }; }
  if (preset === 'this_month') { return { start_date: today.getFullYear()+'-'+String(today.getMonth()+1).padStart(2,'0')+'-01', end_date: today.toISOString().split('T')[0] }; }
  if (preset === 'last_month') { const d = new Date(today.getFullYear(), today.getMonth()-1, 1); const e = new Date(today.getFullYear(), today.getMonth(), 0); return { start_date: d.toISOString().split('T')[0], end_date: e.toISOString().split('T')[0] }; }
  if (preset === 'custom' && dateRange.start && dateRange.end) return { start_date: dateRange.start, end_date: dateRange.end };
  return {};
}

function StatCards({ cards }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 24 }}>
      {cards.map((s, i) => (
        <div key={i} className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="stat-value" style={{ fontSize: 20 }}>{s.value}</span>
            <s.icon size={18} color={s.color} />
          </div>
          <span className="stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function DateFilters({ preset, setPreset, dateRange, setDateRange }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
      <Calendar size={16} color="var(--text-muted)" />
      {PRESETS.map(p => (
        <button key={p.key} className={'btn btn-sm ' + (preset === p.key ? 'btn-primary' : 'btn-secondary')}
          onClick={() => setPreset(p.key)}>{p.label}</button>
      ))}
      {preset === 'custom' && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="date" value={dateRange.start} onChange={e => setDateRange({...dateRange, start: e.target.value})} style={{ width: 150, padding: '6px 10px', fontSize: 12 }} />
          <span style={{ color: 'var(--text-muted)' }}>a</span>
          <input type="date" value={dateRange.end} onChange={e => setDateRange({...dateRange, end: e.target.value})} style={{ width: 150, padding: '6px 10px', fontSize: 12 }} />
        </div>
      )}
    </div>
  );
}

function BreakdownChart({ title, data, dataKey, nameKey, color }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="card">
      <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{title}</h3>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data.slice(0,10)}><CartesianGrid strokeDasharray="3 3" stroke="#252d3d" />
          <XAxis dataKey={nameKey || 'name'} tick={{ fontSize: 10, fill: '#c8d0e0' }} angle={-25} textAnchor="end" height={60} />
          <YAxis tick={tickStyle} tickFormatter={v => '$'+v.toLocaleString()} />
          <Tooltip contentStyle={tooltipStyle} formatter={v => formatMoney(v)} />
          <Bar dataKey={dataKey || 'cost'} name="Inversión" fill={color || '#2E7DE2'} radius={[4,4,0,0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function GoogleAdsTab({ preset, dateRange }) {
  const [data, setData] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [breakdownsSummary, setBreakdownsSummary] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [subtab, setSubtab] = useState('campaigns');
  const [kwSort, setKwSort] = useState({ col: 'cost', dir: 'desc' });

  useEffect(() => { api.get('/campaigns/sync-status').then(res => setSyncStatus(res.data)).catch(() => {}); }, []);

  useEffect(() => {
    const params = { ...getDateParams(preset, dateRange), platform: 'google_ads' };
    api.get('/campaigns/summary', { params }).then(res => setData(res.data)).catch(() => {});
    const dp = getDateParams(preset, dateRange);
    api.get('/campaigns/breakdowns-summary', { params: dp }).then(res => setBreakdownsSummary(res.data)).catch(() => {});
    api.get('/campaigns/keywords', { params: dp }).then(res => setKeywords(res.data)).catch(() => {});
  }, [preset, dateRange]);

  useEffect(() => {
    if (selectedCampaign) {
      const params = getDateParams(preset, dateRange);
      api.get('/campaigns/daily/' + selectedCampaign, { params }).then(res => setDailyData(res.data)).catch(() => {});
    }
  }, [selectedCampaign, preset, dateRange]);

  const statCards = data?.totals ? [
    { label: 'Inversión', value: formatMoney(data.totals.total_cost), icon: DollarSign, color: '#22c55e' },
    { label: 'Impresiones', value: formatNumber(data.totals.total_impressions), icon: Eye, color: '#2E7DE2' },
    { label: 'Clicks', value: formatNumber(data.totals.total_clicks), icon: MousePointer, color: '#f97316' },
    { label: 'CTR', value: (parseFloat(data.totals.avg_ctr)||0).toFixed(2)+'%', icon: TrendingUp, color: '#a855f7' },
    { label: 'CPC', value: formatMoney(data.totals.avg_cpc), icon: DollarSign, color: '#eab308' },
    { label: 'Conv.', value: formatNumber(data.totals.total_conversions), icon: Target, color: '#22c55e' },
  ] : [];

  const deviceData = breakdownsSummary?.byDevice?.map(d => ({ name: d.device.replace('DESKTOP','Escritorio').replace('MOBILE','Móvil').replace('TABLET','Tablet').replace('CONNECTED_TV','TV').replace('OTHER','Otro'), cost: parseFloat(d.cost), clicks: parseInt(d.clicks) })) || [];
  const geoData = breakdownsSummary?.byGeo?.map(g => ({ name: g.location, cost: parseFloat(g.cost), clicks: parseInt(g.clicks) })) || [];
  const ageData = breakdownsSummary?.byAge?.map(a => ({ name: a.age_range.replace('AGE_RANGE_','').replace('_',' - '), cost: parseFloat(a.cost), clicks: parseInt(a.clicks) })) || [];

  if (!data || !data.campaigns?.length) return (
    <div className="empty-state"><Megaphone size={48} /><h3>Sin datos de Google Ads</h3>
      <p style={{ maxWidth: 400, margin: '0 auto' }}>Configurá el script de Google Ads para ver datos acá.</p></div>
  );

  return (
    <>
      {syncStatus && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
          <RefreshCw size={12} /> Última sync: {syncStatus.last_sync || 'Nunca'} | {syncStatus.total_campaigns} campañas | {syncStatus.total_rows} registros
        </div>
      )}

      <StatCards cards={statCards} />

      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {[{k:'campaigns',l:'Campañas'},{k:'breakdowns',l:'Segmentación'},{k:'keywords',l:'Keywords'}].map(t => (
          <button key={t.k} className={'btn btn-sm ' + (subtab === t.k ? 'btn-primary' : 'btn-secondary')} onClick={() => setSubtab(t.k)}>{t.l}</button>
        ))}
      </div>

      {subtab === 'campaigns' && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Inversión por campaña</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.campaigns?.slice(0,10)} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" tick={tickStyle} tickFormatter={v => '$'+v.toLocaleString()} />
                <YAxis dataKey="campaign_name" type="category" width={200} tick={tickStyle} />
                <Tooltip contentStyle={tooltipStyle} formatter={v => formatMoney(v)} />
                <Bar dataKey="total_cost" name="Inversión" radius={[0,6,6,0]}>
                  {data.campaigns?.slice(0,10).map((_,i) => <Cell key={i} fill={campaignColors[i%campaignColors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Detalle de campañas</h3>
            <div className="table-container">
              <table><thead><tr><th>Campaña</th><th>Inversión</th><th>Impresiones</th><th>Clicks</th><th>CTR</th><th>CPC</th><th>Conv.</th><th>Leads CRM</th><th>Ganados</th><th>CPL Real</th><th>CPA</th></tr></thead>
                <tbody>{data.campaigns?.map((c,i) => (
                  <tr key={i} style={{ cursor: 'pointer', background: selectedCampaign === c.campaign_id ? 'var(--accent-glow)' : 'transparent' }}
                    onClick={() => setSelectedCampaign(selectedCampaign === c.campaign_id ? null : c.campaign_id)}>
                    <td style={{ fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.campaign_name}</td>
                    <td>{formatMoney(c.total_cost)}</td><td>{formatNumber(c.total_impressions)}</td><td>{formatNumber(c.total_clicks)}</td>
                    <td>{(parseFloat(c.avg_ctr)||0).toFixed(2)}%</td><td>{formatMoney(c.avg_cpc)}</td><td>{formatNumber(c.total_conversions)}</td>
                    <td style={{ color: '#2E7DE2', fontWeight: 600 }}>{c.crm_leads||0}</td>
                    <td style={{ color: '#22c55e', fontWeight: 600 }}>{c.crm_ganados||0}</td>
                    <td style={{ color: c.cost_per_lead ? '#eab308' : 'var(--text-muted)', fontWeight: 600 }}>{c.cost_per_lead ? formatMoney(c.cost_per_lead) : '—'}</td>
                    <td style={{ color: c.cost_per_client ? '#22c55e' : 'var(--text-muted)', fontWeight: 600 }}>{c.cost_per_client ? formatMoney(c.cost_per_client) : '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
          {selectedCampaign && dailyData.length > 0 && (
            <div className="card">
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
                Rendimiento diario: {data.campaigns?.find(c => c.campaign_id === selectedCampaign)?.campaign_name}
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyData}><CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={tickStyle} /><YAxis yAxisId="left" tick={tickStyle} /><YAxis yAxisId="right" orientation="right" tick={tickStyle} />
                  <Tooltip contentStyle={tooltipStyle} /><Legend wrapperStyle={{ color: '#c8d0e0' }} />
                  <Line yAxisId="left" type="monotone" dataKey="clicks" name="Clicks" stroke="#2E7DE2" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="right" type="monotone" dataKey="cost" name="Inversión" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="left" type="monotone" dataKey="conversions" name="Conversiones" stroke="#f97316" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}

      {subtab === 'breakdowns' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <BreakdownChart title="Por dispositivo" data={deviceData} color="#2E7DE2" />
          <BreakdownChart title="Por rango de edad" data={ageData} color="#a855f7" />
          <BreakdownChart title="Por ubicación geográfica" data={geoData} color="#22c55e" />
        </div>
      )}

      {subtab === 'keywords' && (() => {
        const kwCols = [
          { key: 'keyword_text', label: 'Keyword' }, { key: 'campaign_name', label: 'Campaña' }, { key: 'match_type', label: 'Tipo' },
          { key: 'cost', label: 'Inversión', num: true }, { key: 'clicks', label: 'Clicks', num: true }, { key: 'impressions', label: 'Impresiones', num: true },
          { key: 'ctr', label: 'CTR', num: true }, { key: 'cpc', label: 'CPC', num: true }, { key: 'conversions', label: 'Conv.', num: true }, { key: 'quality_score', label: 'QS', num: true }
        ];
        const sortedKw = [...keywords].sort((a, b) => {
          const av = a[kwSort.col], bv = b[kwSort.col];
          if (av == null) return 1; if (bv == null) return -1;
          const cmp = typeof av === 'string' ? av.localeCompare(bv) : parseFloat(av) - parseFloat(bv);
          return kwSort.dir === 'asc' ? cmp : -cmp;
        });
        const toggleSort = (col) => setKwSort(prev => ({ col, dir: prev.col === col && prev.dir === 'desc' ? 'asc' : 'desc' }));
        return (
          <div className="card">
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><Search size={18} /> Keywords ({keywords.length})</h3>
            {keywords.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>Sin datos de keywords. Ejecutá el script de Google Ads para traerlos.</p>
            ) : (
              <div className="table-container">
                <table><thead><tr>{kwCols.map(c => (
                  <th key={c.key} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(c.key)}>
                    {c.label} {kwSort.col === c.key ? (kwSort.dir === 'desc' ? '▼' : '▲') : ''}
                  </th>
                ))}</tr></thead>
                  <tbody>{sortedKw.slice(0,100).map((k,i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{k.keyword_text}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.campaign_name}</td>
                      <td><span className="badge" style={{ fontSize: 10, background: 'rgba(46,125,226,0.15)', color: '#2E7DE2' }}>{(k.match_type||'').replace('EXACT','Exacta').replace('PHRASE','Frase').replace('BROAD','Amplia')}</span></td>
                      <td>{formatMoney(k.cost)}</td><td>{formatNumber(k.clicks)}</td><td>{formatNumber(k.impressions)}</td>
                      <td>{(parseFloat(k.ctr)||0).toFixed(2)}%</td><td>{formatMoney(k.cpc)}</td>
                      <td>{formatNumber(k.conversions)}</td>
                      <td style={{ color: k.quality_score >= 7 ? '#22c55e' : k.quality_score >= 4 ? '#eab308' : k.quality_score ? '#ef4444' : 'var(--text-muted)' }}>{k.quality_score || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}
    </>
  );
}

function MetaAdsTab({ preset, dateRange }) {
  const [data, setData] = useState(null);
  const [metaStatus, setMetaStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [actions, setActions] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get('/meta-ads/status').then(res => setMetaStatus(res.data)).catch(() => setMetaStatus({ connected: false })); }, []);

  useEffect(() => {
    setLoading(true);
    const params = getDateParams(preset, dateRange);
    api.get('/meta-ads/summary', { params }).then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [preset, dateRange]);

  useEffect(() => {
    if (selectedCampaign) {
      const params = getDateParams(preset, dateRange);
      api.get('/meta-ads/actions/' + selectedCampaign, { params }).then(res => setActions(res.data)).catch(() => {});
    }
  }, [selectedCampaign, preset, dateRange]);

  const handleSync = async (days) => {
    setSyncing(true);
    try {
      const res = await api.post('/meta-ads/sync', { days_back: days });
      toast.success('Sincronizado: ' + res.data.synced_general + ' registros, ' + res.data.synced_breakdowns + ' breakdowns');
      const params = getDateParams(preset, dateRange);
      api.get('/meta-ads/summary', { params }).then(r => setData(r.data));
      api.get('/meta-ads/status').then(r => setMetaStatus(r.data));
    } catch (err) { toast.error(err.response?.data?.error || 'Error al sincronizar'); }
    finally { setSyncing(false); }
  };

  const statCards = data?.totals ? [
    { label: 'Inversión', value: formatMoney(data.totals.cost), icon: DollarSign, color: '#22c55e' },
    { label: 'Impresiones', value: formatNumber(data.totals.impressions), icon: Eye, color: '#2E7DE2' },
    { label: 'Clicks', value: formatNumber(data.totals.clicks), icon: MousePointer, color: '#f97316' },
    { label: 'Conv.', value: formatNumber(data.totals.conversions), icon: Target, color: '#a855f7' },
  ] : [];

  const placementData = data?.byPlacement?.map(p => ({ name: p.placement.replace(/_/g,' '), cost: parseFloat(p.cost), clicks: parseInt(p.clicks) })) || [];
  const ageGenderData = data?.byAgeGender?.map(a => ({ name: a.age_gender.replace('_',' '), cost: parseFloat(a.cost), clicks: parseInt(a.clicks) })) || [];
  const countryData = data?.byCountry?.map(c => ({ name: c.country, cost: parseFloat(c.cost), clicks: parseInt(c.clicks), impressions: parseInt(c.impressions) })) || [];
  const regionData = data?.byRegion?.map(r => ({ name: r.region, cost: parseFloat(r.cost), clicks: parseInt(r.clicks) })) || [];

  if (!metaStatus?.connected && !data?.campaigns?.length && !loading) return (
    <div className="empty-state"><Megaphone size={48} /><h3>Meta Ads no configurado</h3>
      <p style={{ maxWidth: 400, margin: '0 auto' }}>Configurá el token y la cuenta publicitaria de Meta Ads en la sección de Configuración.</p></div>
  );

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando datos de Meta Ads...</div>;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={12} /> {metaStatus?.connected ? <>Cuenta: {metaStatus.account_name} ({metaStatus.currency}) | </> : ''}Última sync: {metaStatus?.last_sync || 'Nunca'} | {metaStatus?.total_campaigns || data?.campaigns?.length || 0} campañas
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => handleSync(30)} disabled={syncing}>
            {syncing ? <Loader size={14} className="spin" /> : <RefreshCw size={14} />} Sync 30 días
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => handleSync(90)} disabled={syncing}>Sync 90 días</button>
        </div>
      </div>

      {!data?.campaigns?.length ? (
        <div className="empty-state"><Megaphone size={48} /><h3>Sin datos de Meta Ads</h3><p>Hacé click en "Sync" para traer los datos.</p></div>
      ) : (
        <>
          <StatCards cards={statCards} />

          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Campañas de Meta Ads</h3>
            <div className="table-container">
              <table><thead><tr><th>Campaña</th><th>Estado</th><th>Inversión</th><th>Impresiones</th><th>Clicks</th><th>CTR</th><th>CPC</th><th>CPM</th><th>Conv.</th><th>Leads CRM</th><th>CPL Real</th></tr></thead>
                <tbody>{data.campaigns.map((c,i) => (
                  <tr key={i} style={{ cursor: 'pointer', background: selectedCampaign === c.campaign_id ? 'var(--accent-glow)' : 'transparent' }}
                    onClick={() => setSelectedCampaign(selectedCampaign === c.campaign_id ? null : c.campaign_id)}>
                    <td style={{ fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.campaign_name}</td>
                    <td><span className="badge" style={{ background: c.campaign_status==='ACTIVE' ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.15)', color: c.campaign_status==='ACTIVE' ? '#22c55e' : '#64748b', fontSize: 11 }}>{c.campaign_status==='ACTIVE' ? 'Activa' : c.campaign_status==='PAUSED' ? 'Pausada' : c.campaign_status}</span></td>
                    <td>{formatMoney(c.total_cost)}</td><td>{formatNumber(c.total_impressions)}</td><td>{formatNumber(c.total_clicks)}</td>
                    <td>{(parseFloat(c.avg_ctr)||0).toFixed(2)}%</td><td>{formatMoney(c.avg_cpc)}</td><td>{formatMoney(c.avg_cpm)}</td>
                    <td>{formatNumber(c.total_conversions)}</td>
                    <td style={{ color: '#2E7DE2', fontWeight: 600 }}>{c.crm_leads||0}</td>
                    <td style={{ color: c.cost_per_lead ? '#eab308' : 'var(--text-muted)', fontWeight: 600 }}>{c.cost_per_lead ? formatMoney(c.cost_per_lead) : '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <BreakdownChart title="Por plataforma / ubicación" data={placementData} color="#2E7DE2" />
            <BreakdownChart title="Por edad y género" data={ageGenderData} color="#a855f7" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            {regionData.length > 0 && <BreakdownChart title="Por región / provincia" data={regionData} color="#22c55e" />}
            {countryData.length > 0 && <BreakdownChart title="Por país" data={countryData} color="#f97316" />}
          </div>

          {selectedCampaign && actions && actions.length > 0 && (
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
                Acciones: {data.campaigns.find(c => c.campaign_id === selectedCampaign)?.campaign_name}
              </h3>
              <div className="table-container">
                <table><thead><tr><th>Tipo de acción</th><th>Cantidad</th><th>Costo por acción</th></tr></thead>
                  <tbody>{actions.map((a,i) => (
                    <tr key={i}><td style={{ fontWeight: 500 }}>{a.action_type.replace(/_/g,' ')}</td>
                      <td>{formatNumber(a.total)}</td><td>{a.total_cost ? formatMoney(a.total_cost / a.total) : '—'}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default function Campaigns() {
  const [platform, setPlatform] = useState('google_ads');
  const [preset, setPreset] = useState('last_30d');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Megaphone size={24} color="var(--accent)" />
          <h1 style={{ fontSize: 26, fontWeight: 800 }}>Campañas</h1>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className={'btn btn-sm ' + (platform === 'google_ads' ? 'btn-primary' : 'btn-secondary')}
            onClick={() => setPlatform('google_ads')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 16 }}>G</span> Google Ads
          </button>
          <button className={'btn btn-sm ' + (platform === 'meta_ads' ? 'btn-primary' : 'btn-secondary')}
            onClick={() => setPlatform('meta_ads')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 16 }}>M</span> Meta Ads
          </button>
        </div>
      </div>
      <DateFilters preset={preset} setPreset={setPreset} dateRange={dateRange} setDateRange={setDateRange} />
      {platform === 'google_ads' ? <GoogleAdsTab preset={preset} dateRange={dateRange} /> : <MetaAdsTab preset={preset} dateRange={dateRange} />}
    </div>
  );
}
